import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { isEmpty, isNil } from "ramda";

import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Avatar,
  Input,
  Select,
  MenuItem,
  FormControlLabel,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Autocomplete,
  Checkbox,
  Tooltip,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import { ThemeProvider } from "@mui/material/styles";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import HelpIcon from "@mui/icons-material/Help";

import avatar from "../../../../../images/avatar.png";
import palette from "../../../../../config/colorPalette";
import { getImageSrc, getMuiTheme, validateEmail, validateSecondPassword } from "../../../../utils/utils";
import { handleChanges, handleClose, handleModalAccept } from "./utils/utils";
import { AvatarUploadBtn, ErrorText } from "./styled";

const UserModal = ({ open, setOpen, user, liveUser, roles, type, acceptFunction, testId, configData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const inputRef = useRef();
  const theme = useMemo(() => getMuiTheme(), []);
  const [localUser, setLocalUser] = useState(user);
  const [password, setPassword] = useState("");
  const [secondPassword, setSecondPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showSecondPassword, setShowSecondPassword] = useState(false);
  const [usernameError, setUsernameError] = useState();
  const [passwordError, setPasswordError] = useState();
  const [secondPasswordError, setSecondPasswordError] = useState();
  const [passwordCheck, setPasswordCheck] = useState(true);
  const [serviceList, setServiceList] = useState([]);
  const assetsData = useSelector((state) => state.assetsData);

  const readOnly = type !== "edit" && type !== "create";
  const alertTypes = useMemo(() => {
    return [
      { label: "none", value: 0 },
      { label: "low", value: 1 },
      { label: "intermediate", value: 2 },
      { label: "high", value: 3 },
    ];
  }, []);
  const disabled = useMemo(
    () =>
      !validateEmail(localUser?.username) ||
      (type === "create" && passwordCheck && !validateSecondPassword(password, secondPassword)) ||
      isNil(localUser?.role) ||
      isNil(localUser?.demarcations),
    [localUser, type, password, secondPassword, passwordCheck],
  );
  useEffect(() => {
    if (type === "create" && localUser) {
      const shouldUpdate =
        localUser.role === undefined ||
        localUser.demarcations === undefined ||
        localUser.equipmentAlerts !== false ||
        localUser.measurementAlerts === undefined ||
        localUser.assetsConfigurations;

      if (shouldUpdate) {
        setLocalUser((prevUser) => ({
          ...prevUser,
          role: prevUser.role || "viewer",
          demarcations: Array.isArray(prevUser.demarcations) ? prevUser.demarcations : [],
          equipmentAlerts: prevUser.equipmentAlerts ?? false,
          measurementAlerts: prevUser.measurementAlerts ?? 0,
          assetsConfigurations: prevUser.assetsConfigurations ?? {},
        }));
      }
    } else if (!isNil(localUser) && !isNil(localUser.loginServices)) {
      const services = [];
      for (const key in configData.loginServices) {
        if (configData.loginServices.hasOwnProperty(key) && configData.loginServices[key].active) {
          services.push(key);
        }
      }
      let auxCheck = false;
      if (configData.loginServices[localUser.loginService].check_fields.includes("password")) {
        auxCheck = true;
      }
      setPasswordCheck(auxCheck);
      setServiceList(services);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localUser]);

  useMemo(() => {
    if (!isNil(configData) && isEmpty(configData.demarcations)) {
      setLocalUser({ ...localUser, demarcations: ["Global"], role: type === "create" ? "viewer" : localUser?.role });
    } else if (!isNil(configData) && !isEmpty(configData.demarcations)) {
      setLocalUser({ ...localUser, role: type === "create" ? "viewer" : localUser?.role });
    }
    if (!isNil(configData) && !isNil(configData.loginServices)) {
      const services = [];
      for (const key in configData.loginServices) {
        if (configData.loginServices.hasOwnProperty(key) && configData.loginServices[key].active) {
          services.push(key);
        }
      }
      if (type === "create") setLocalUser({ ...localUser, loginService: services[0] });
      setServiceList(services);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configData]);
  return (
    <div>
      <ThemeProvider theme={theme}>
        <Dialog maxWidth="md" open={open} onClose={() => handleClose(setLocalUser, setOpen)}>
          <DialogTitle style={{ fontSize: "x-large", color: palette.primary, fontWeight: "600" }}>
            {`${intl.formatMessage({ id: type })} ${intl.formatMessage({ id: "user" })}`}
          </DialogTitle>
          <DialogContent style={{ paddingBottom: "0" }}>
            <form autoComplete="off">
              <div>
                <Divider style={{ marginBottom: "1rem", color: palette.primary }} />
                <div style={{ display: "flex" }}>
                  <div>
                    <TextField
                      autoFocus
                      variant="outlined"
                      autoComplete="off"
                      margin="dense"
                      id="name"
                      label={intl.formatMessage({ id: "name" })}
                      type="text"
                      fullWidth
                      value={localUser?.name ?? ""}
                      onChange={(event) => setLocalUser({ ...localUser, name: event.target.value })}
                      disabled={readOnly}
                    />
                    <FormControl fullWidth variant="outlined" margin="dense">
                      <InputLabel htmlFor="email">{intl.formatMessage({ id: "email" })}*</InputLabel>
                      <OutlinedInput
                        id="email"
                        type="email"
                        required
                        autoComplete="off"
                        endAdornment={
                          <InputAdornment position="end">
                            <AlternateEmailIcon color="#e4e6ef" />
                          </InputAdornment>
                        }
                        disabled={readOnly}
                        label={`${intl.formatMessage({ id: "email" })}*`}
                        value={localUser?.username ?? ""}
                        onChange={(event) =>
                          handleChanges({
                            who: "email",
                            value: event.target.value,
                            setError: setUsernameError,
                            setData: (value) => setLocalUser({ ...localUser, username: value }),
                            intl,
                          })
                        }
                        error={usernameError ? true : false}
                        data-testid={`${testId}-email`}
                      />
                      {!isNil(usernameError) && <ErrorText>{intl.formatMessage({ id: usernameError })}</ErrorText>}
                    </FormControl>
                    <TextField
                      margin="dense"
                      autoComplete="off"
                      variant="outlined"
                      id="phone"
                      label={intl.formatMessage({ id: "phone" })}
                      type="text"
                      fullWidth
                      value={localUser?.phone ?? ""}
                      onChange={(event) => setLocalUser({ ...localUser, phone: event.target.value })}
                      disabled={readOnly}
                    />
                    <div style={{ display: "flex", alignItems: "center", marginTop: "0.5rem" }}>
                      <div style={{ minWidth: "10rem" }}>{intl.formatMessage({ id: "select.role" })}*:</div>
                      <Select
                        value={localUser?.role ?? "viewer"}
                        id="select_role"
                        onChange={(event) => {
                          const assetconfig = {};
                          if (
                            isNil(localUser.assetsConfigurations) &&
                            event.target.value !== "admin" &&
                            event.target.value !== "administrator"
                          ) {
                            const demarcationList = localUser.demarcations.includes("Global")
                              ? [configData.demarcations[0]]
                              : localUser.demarcations;

                            const newRole = event.target.value;

                            for (let indexDem = 0; indexDem < demarcationList.length; indexDem++) {
                              const updatedAssetsConfigurations = { ...localUser.assetsConfigurations };
                              const demarcation = demarcationList[indexDem];
                              const assets = assetsData.data.filter((asset) => asset.demarcation === demarcation);

                              if (isNil(updatedAssetsConfigurations[demarcation])) {
                                const assetsObject = assets.reduce((acc, asset) => {
                                  acc[asset.name] = "viewer";
                                  return acc;
                                }, {});

                                updatedAssetsConfigurations[demarcation] = {
                                  assets: assetsObject,
                                };
                              }

                              updatedAssetsConfigurations[demarcation].role = newRole;

                              assetconfig[demarcation] = updatedAssetsConfigurations[demarcation];
                            }
                          }

                          setLocalUser({
                            ...localUser,
                            role: event.target.value,
                            assetsConfigurations: !isEmpty(assetconfig) ? assetconfig : localUser.assetsConfigurations,
                            demarcations:
                              event.target.value === "admin" || event.target.value === "administrator"
                                ? ["Global"]
                                : localUser.demarcations.includes("Global")
                                ? [configData.demarcations[0]]
                                : localUser.demarcations,
                          });
                        }}
                        fullWidth
                        disabled={readOnly}
                      >
                        {!isNil(roles) &&
                          roles
                            .filter((role) => role.type !== "equipment")
                            .map((role) => (
                              <MenuItem id={role.type} value={role.type}>
                                {role.type}
                              </MenuItem>
                            ))}
                      </Select>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", margin: "0.5rem 0" }}>
                      <div style={{ minWidth: "10rem", display: "flex", alignItems: "center" }}>
                        {intl.formatMessage({ id: "alerts type" })}*:
                        <Tooltip
                          title={
                            <span style={{ fontSize: "1.25rem" }}>{intl.formatMessage({ id: "alarm.tooltip" })}</span>
                          }
                        >
                          <HelpIcon style={{ marginLeft: "0.5rem" }} />
                        </Tooltip>
                      </div>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={localUser?.measurementAlerts !== 0}
                            disabled={readOnly}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setLocalUser({ ...localUser, measurementAlerts: 1 });
                              } else {
                                setLocalUser({ ...localUser, measurementAlerts: 0 });
                              }
                            }}
                          />
                        }
                        label={intl.formatMessage({ id: "measurement" })}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={localUser?.equipmentAlerts}
                            disabled={readOnly}
                            onChange={(event) => {
                              setLocalUser({ ...localUser, equipmentAlerts: event.target.checked });
                            }}
                          />
                        }
                        label={intl.formatMessage({ id: "equipment" })}
                      />
                    </div>

                    {!isNil(localUser?.measurementAlerts) && localUser?.measurementAlerts !== 0 && (
                      <div style={{ display: "flex", alignItems: "center", margin: "0.5rem 0" }}>
                        <div style={{ minWidth: "10rem" }}>{intl.formatMessage({ id: "alerts level" })}*:</div>
                        <Select
                          value={localUser?.measurementAlerts ?? 0}
                          defaultValue={localUser?.measurementAlerts ?? 0}
                          onChange={(event) => setLocalUser({ ...localUser, measurementAlerts: event.target.value })}
                          disabled={readOnly}
                        >
                          {!isNil(alertTypes) &&
                            alertTypes.map((item) => {
                              if (item.value === 0) {
                                return null;
                              }
                              return <MenuItem value={item.value}>{intl.formatMessage({ id: item.label })}</MenuItem>;
                            })}
                        </Select>
                      </div>
                    )}

                    {!isNil(configData) && !isNil(configData.demarcations) && !isEmpty(configData.demarcations) ? (
                      <div style={{ display: "flex", alignItems: "center", marginTop: "0.5rem" }}>
                        <div style={{ minWidth: "10rem" }}>{intl.formatMessage({ id: "select.demarcation" })}*:</div>
                        {localUser?.role === "admin" || localUser?.role === "administrator" ? (
                          <h1>Global</h1>
                        ) : (
                          <Autocomplete
                            multiple
                            id="multiple-checkboxes"
                            options={configData.demarcations}
                            disableCloseOnSelect
                            disabled={readOnly}
                            onChange={(event, values) => {
                              const newAssetConfig = localUser.assetsConfigurations;
                              const newRole = localUser.role;

                              for (let indexDem = 0; indexDem < values.length; indexDem++) {
                                const updatedAssetsConfigurations = { ...localUser.assetsConfigurations };
                                const demarcation = values[indexDem];
                                if (isNil(localUser.assetsConfigurations[demarcation])) {
                                  const assets = assetsData.data.filter((asset) => asset.demarcation === demarcation);

                                  if (isNil(updatedAssetsConfigurations[demarcation])) {
                                    const assetsObject = assets.reduce((acc, asset) => {
                                      acc[asset.name] = "viewer";
                                      return acc;
                                    }, {});

                                    updatedAssetsConfigurations[demarcation] = {
                                      assets: assetsObject,
                                    };
                                  }

                                  updatedAssetsConfigurations[demarcation].role = newRole;

                                  newAssetConfig[demarcation] = updatedAssetsConfigurations[demarcation];
                                }
                              }
                              setLocalUser({
                                ...localUser,
                                demarcations: values,
                                assetsConfigurations: newAssetConfig,
                              });
                            }}
                            getOptionLabel={(option) => option}
                            renderOption={(props, option, { selected }) => (
                              <li {...props}>
                                <Checkbox style={{ marginRight: 8 }} checked={selected} />
                                {intl.formatMessage({ id: option })}
                              </li>
                            )}
                            value={isNil(localUser?.demarcations) ? [] : localUser.demarcations}
                            style={{ width: 300 }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label={intl.formatMessage({ id: "demarcations" })}
                              />
                            )}
                          />
                        )}
                      </div>
                    ) : null}

                    <TextField
                      margin="dense"
                      autoComplete="off"
                      variant="outlined"
                      id="organization"
                      label={intl.formatMessage({ id: "organization" })}
                      type="text"
                      fullWidth
                      value={localUser?.organization ?? ""}
                      onChange={(event) => setLocalUser({ ...localUser, organization: event.target.value })}
                      disabled={readOnly}
                    />
                    {!localUser?.demarcations?.includes("Global") && !isEmpty(localUser?.demarcations) && (
                      <table style={{ borderCollapse: "collapse", width: "100%" }}>
                        <tr>
                          <th>Demarcation</th>
                          <th>Default Role</th>
                          <th>Asset</th>
                          <th>Role</th>
                        </tr>
                        {localUser?.demarcations?.map((demarcation) => {
                          const currentRole = localUser.role;
                          const demRole =
                            !isNil(localUser.assetsConfigurations) &&
                            !isNil(localUser.assetsConfigurations[demarcation])
                              ? localUser.assetsConfigurations[demarcation].role
                              : currentRole;
                          const assets = assetsData.data.filter((asset) => asset.demarcation === demarcation);
                          const currentRoleData = roles.find((role) => currentRole === role.type);
                          const printRoles = roles.filter(
                            (role) => role.order <= currentRoleData.order && role.type !== "equipment",
                          );
                          //HABRÁ QUE CAMBIAR A TODOS LOS QUE TENGAN ROLES POR ENCIMA AL MAXIMO DISPONIBLE

                          return assets.map((asset, assetIndex) => {
                            if (assetIndex === 0) {
                              // Primera fila con rowSpan
                              return (
                                <tr key={`${demarcation}-first`}>
                                  <td rowSpan={assets.length}>
                                    <center>{demarcation}</center>
                                  </td>
                                  <td rowSpan={assets.length}>
                                    <Select
                                      value={demRole}
                                      onChange={(event) => {
                                        setLocalUser((prevLocalUser) => {
                                          const updatedAssetsConfigurations = { ...prevLocalUser.assetsConfigurations };

                                          // Actualizar el role principal de la demarcación
                                          const newRole = event.target.value;
                                          if (isNil(updatedAssetsConfigurations[demarcation])) {
                                            const assetsObject = assets.reduce((acc, asset) => {
                                              acc[asset.name] = "viewer";
                                              return acc;
                                            }, {});

                                            updatedAssetsConfigurations[demarcation] = {
                                              assets: assetsObject,
                                            };
                                          }
                                          updatedAssetsConfigurations[demarcation].role = newRole;

                                          // Obtener el máximo rol disponible para los assets según el nuevo role
                                          const maxAllowedRoleOrder = roles.find((role) => role.type === newRole).order;
                                          const availableRoles = roles
                                            .filter((role) => role.order <= maxAllowedRoleOrder)
                                            .map((role) => role.type);
                                          const maxAllowedRole = availableRoles[availableRoles.length - 1];

                                          // Ajustar los roles de los assets si están por encima del máximo permitido
                                          Object.keys(updatedAssetsConfigurations[demarcation].assets).forEach(
                                            (assetName) => {
                                              const currentAssetRole =
                                                updatedAssetsConfigurations[demarcation].assets[assetName];
                                              if (!availableRoles.includes(currentAssetRole)) {
                                                // Asignar el rol máximo permitido si el actual no es válido
                                                updatedAssetsConfigurations[demarcation].assets[assetName] =
                                                  maxAllowedRole;
                                              }
                                            },
                                          );

                                          return {
                                            ...prevLocalUser,
                                            assetsConfigurations: updatedAssetsConfigurations,
                                          };
                                        });
                                      }}
                                      disabled={readOnly}
                                    >
                                      {printRoles.map((pr, roleIndex) => (
                                        <MenuItem key={roleIndex} value={pr.type}>
                                          {pr.type}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </td>
                                  <td>
                                    <center>{asset.name}</center>
                                  </td>
                                  <td>
                                    <center>
                                      <Select
                                        defaultValue={currentRole}
                                        value={
                                          !isNil(localUser.assetsConfigurations) &&
                                          !isEmpty(localUser.assetsConfigurations)
                                            ? localUser.assetsConfigurations[demarcation].assets[asset.name]
                                            : "viewer"
                                        }
                                        onChange={(event) => {
                                          setLocalUser((prevLocalUser) => {
                                            // Copiar el estado actual
                                            const updatedAssetsConfigurations = {
                                              ...prevLocalUser.assetsConfigurations,
                                            };

                                            // Asegurar la estructura para la demarcation actual
                                            if (!updatedAssetsConfigurations[demarcation]) {
                                              updatedAssetsConfigurations[demarcation] = {
                                                role: "viewer", // Valor por defecto para role si no existe
                                                assets: {},
                                              };
                                            }

                                            // Actualizar el asset específico
                                            updatedAssetsConfigurations[demarcation].assets[asset.name] =
                                              event.target.value;

                                            return {
                                              ...prevLocalUser,
                                              assetsConfigurations: updatedAssetsConfigurations,
                                            };
                                          });
                                        }}
                                        disabled={
                                          currentRole === "viewer" ||
                                          (!isNil(localUser.assetsConfigurations) &&
                                            localUser.assetsConfigurations[demarcation].role === "viewer") ||
                                          readOnly
                                        }
                                      >
                                        {printRoles.map((pr, roleIndex) => {
                                          const demRole = !isNil(localUser.assetsConfigurations)
                                            ? localUser.assetsConfigurations[demarcation].role
                                            : localUser.role;
                                          const demRoleData = roles.find((r) => r.type === demRole);
                                          if (pr.order > demRoleData.order && pr.order > 1) {
                                            return null;
                                          }
                                          return (
                                            <MenuItem key={`${asset}_${assetIndex}_${pr}_${roleIndex}`} value={pr.type}>
                                              {pr.type}
                                            </MenuItem>
                                          );
                                        })}
                                      </Select>
                                    </center>
                                  </td>
                                </tr>
                              );
                            }

                            // Filas adicionales sin rowSpan
                            return (
                              <tr key={`${demarcation}-${assetIndex}`}>
                                <td>
                                  <center>{asset.name}</center>
                                </td>
                                <td>
                                  <center>
                                    <Select
                                      defaultValue={currentRole}
                                      value={
                                        !isNil(localUser.assetsConfigurations) &&
                                        !isEmpty(localUser.assetsConfigurations)
                                          ? localUser.assetsConfigurations[demarcation].assets[asset.name]
                                          : "viewer"
                                      }
                                      onChange={(event) => {
                                        setLocalUser((prevLocalUser) => {
                                          // Copiar el estado actual
                                          const updatedAssetsConfigurations = {
                                            ...prevLocalUser.assetsConfigurations,
                                          };

                                          // Asegurar la estructura para la demarcation actual
                                          if (!updatedAssetsConfigurations[demarcation]) {
                                            updatedAssetsConfigurations[demarcation] = {
                                              role: "viewer", // Valor por defecto para role si no existe
                                              assets: {},
                                            };
                                          }

                                          // Actualizar el asset específico
                                          updatedAssetsConfigurations[demarcation].assets[asset.name] =
                                            event.target.value;

                                          return {
                                            ...prevLocalUser,
                                            assetsConfigurations: updatedAssetsConfigurations,
                                          };
                                        });
                                      }}
                                      disabled={
                                        currentRole === "viewer" ||
                                        (!isNil(localUser.assetsConfigurations) &&
                                          localUser.assetsConfigurations[demarcation].role === "viewer") ||
                                        readOnly
                                      }
                                    >
                                      {printRoles.map((pr, roleIndex) => {
                                        const demRole = !isNil(localUser.assetsConfigurations)
                                          ? localUser.assetsConfigurations[demarcation].role
                                          : localUser.role;
                                        const demRoleData = roles.find((r) => r.type === demRole);
                                        if (pr.order > demRoleData.order && pr.order > 1) {
                                          return null;
                                        }
                                        return (
                                          <MenuItem key={`${asset}_${assetIndex}_${pr}_${roleIndex}`} value={pr.type}>
                                            {pr.type}
                                          </MenuItem>
                                        );
                                      })}
                                    </Select>
                                  </center>
                                </td>
                              </tr>
                            );
                          });
                        })}
                      </table>
                    )}
                  </div>
                  <div
                    style={{
                      margin: "0 1rem",
                      minWidth: "15rem",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}
                    >
                      <Avatar
                        alt="avatar"
                        src={localUser?.avatar ? getImageSrc(localUser.avatar) : avatar}
                        sx={{ width: 136, height: 136 }}
                      />
                      {!readOnly && (
                        <>
                          <Input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            id="avatar-input"
                            onChange={(event) => {
                              const file = event.target.files[0];
                              if (file && file.type.startsWith("image/")) {
                                const reader = new FileReader();
                                reader.onload = () => {
                                  const base64String = reader.result;
                                  setLocalUser({ ...localUser, avatar: base64String.split("base64,")?.[1] });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                          <AvatarUploadBtn style={{ color: palette.dark, backgroundColor: `${palette.primary}70` }}>
                            <label htmlFor="avatar-input">
                              <Button variant="standard" component="span" onClick={() => inputRef.current.click()}>
                                <PhotoCameraIcon />
                              </Button>
                            </label>
                          </AvatarUploadBtn>
                        </>
                      )}
                    </div>
                    {(type === "create" || liveUser.role === "admin") && (
                      <div style={{ marginRight: "5%" }}>
                        {passwordCheck && (
                          <>
                            {" "}
                            <FormControl fullWidth variant="outlined" margin="dense">
                              <InputLabel htmlFor="password">{intl.formatMessage({ id: "password" })}*</InputLabel>
                              <OutlinedInput
                                id="password"
                                type={showPassword ? "text" : "password"}
                                autoComplete="new-password"
                                endAdornment={
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="visibility"
                                      onClick={() => setShowPassword(!showPassword)}
                                      onMouseDown={(event) => event.preventDefault()}
                                      edge="end"
                                      data-testid={`${testId}-visibility`}
                                    >
                                      {showPassword ? (
                                        <VisibilityOff color="#e4e6ef" />
                                      ) : (
                                        <Visibility color="#e4e6ef" />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                }
                                label={`${intl.formatMessage({ id: "password" })}*`}
                                value={password ?? ""}
                                onChange={(event) =>
                                  handleChanges({
                                    who: "password",
                                    value: event.target.value,
                                    setError: setPasswordError,
                                    setData: setPassword,
                                    intl,
                                  })
                                }
                                error={passwordError ? true : false}
                                data-testid={`${testId}-password`}
                              />
                              {!isNil(passwordError) && (
                                <ErrorText>{intl.formatMessage({ id: passwordError })}</ErrorText>
                              )}
                            </FormControl>
                            <FormControl fullWidth variant="outlined" margin="dense">
                              <InputLabel htmlFor="secondPassword">
                                {intl.formatMessage({ id: "repeat password" })}*
                              </InputLabel>
                              <OutlinedInput
                                id="secondPassword"
                                type={showSecondPassword ? "text" : "password"}
                                autoComplete="off"
                                endAdornment={
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="visibility"
                                      onClick={() => setShowSecondPassword(!showSecondPassword)}
                                      onMouseDown={(event) => event.preventDefault()}
                                      edge="end"
                                      data-testid={`${testId}-visibility`}
                                    >
                                      {showSecondPassword ? (
                                        <VisibilityOff color="#e4e6ef" />
                                      ) : (
                                        <Visibility color="#e4e6ef" />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                }
                                label={`${intl.formatMessage({ id: "repeat password" })}*`}
                                value={secondPassword ?? ""}
                                onChange={(event) =>
                                  handleChanges({
                                    who: "secondPassword",
                                    value: event.target.value,
                                    setData: setSecondPassword,
                                    setError: setSecondPasswordError,
                                    password,
                                    intl,
                                  })
                                }
                                error={secondPasswordError ? true : false}
                                data-testid={`${testId}-password`}
                              />
                              {!isNil(secondPasswordError) && (
                                <ErrorText>{intl.formatMessage({ id: secondPasswordError })}</ErrorText>
                              )}
                            </FormControl>
                          </>
                        )}

                        <TextField
                          id="select_service"
                          variant="outlined"
                          autoComplete="off"
                          margin="dense"
                          label={intl.formatMessage({ id: "services" })}
                          value={localUser?.loginService ?? serviceList[0]}
                          onChange={(event) => {
                            if (configData.loginServices[event.target.value].check_fields.includes("password")) {
                              setPasswordCheck(true);
                            } else {
                              setPasswordCheck(false);
                            }
                            setLocalUser({ ...localUser, loginService: event.target.value });
                          }}
                          disabled={readOnly}
                          select
                        >
                          {!isNil(serviceList) &&
                            serviceList.map((service) => (
                              <MenuItem id={service} value={service}>
                                {service.toUpperCase()}
                              </MenuItem>
                            ))}
                        </TextField>
                      </div>
                    )}
                  </div>
                </div>
                <Divider style={{ marginTop: "1rem" }} />
              </div>
            </form>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              style={{ backgroundColor: palette.secondary, color: "white" }}
              onClick={() => handleClose(setLocalUser, setOpen)}
            >
              {intl.formatMessage({ id: type === "view" ? "close" : "cancel" })}
            </Button>
            {(type === "edit" || type === "create") && (
              <Button
                variant="contained"
                style={{
                  backgroundColor: disabled ? palette.light : palette.primary,
                }}
                id={"save_data_button"}
                disabled={disabled}
                onClick={async () => {
                  const result = await handleModalAccept(
                    { ...localUser, order: localUser.order ?? 0, password: !isEmpty(password) ? password : null },
                    type,
                    dispatch,
                    navigate,
                    intl,
                  );
                  if (result) {
                    acceptFunction();
                    handleClose(setLocalUser, setOpen);
                  }
                }}
              >
                {intl.formatMessage({ id: "accept" })}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </div>
  );
};

export default UserModal;
